import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Box,
} from "@mui/material";
import { CreateTenantRequest, TenantOperation } from "../../../types/tenant";
import isValidAbsoluteUrl from "../../../services/validationHelper";
import { useSearchParams } from "react-router-dom";

interface OwnProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: CreateTenantRequest) => void;
}

const CreateTenantDialog = ({ open, onClose, onSave }: OwnProps) => {
  const [searchParams] = useSearchParams();
  const defaultCreateTenantRequest = useMemo(
    () => ({
      name: "",
      environmentId: searchParams.get("environmentid") || "",
      archiveId: searchParams.get("archiveid")
        ? parseInt(searchParams.get("archiveid") || "")
        : 0,
      hdmApiEndpoint: searchParams.get("hdmapiendpoint") || "",
      operations: [],
      hdmCredentials: {
        username: "",
        password: "",
      },
    }),
    [searchParams]
  );

  const [formData, setFormData] = useState<CreateTenantRequest>(
    defaultCreateTenantRequest
  );

  const [errors, setErrors] = useState({
    name: false,
    environmentId: false,
    archiveId: false,
    hdmApiEndpoint: false,
    username: false,
    password: false,
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: !value.trim() }));
  };

  const handleHdmCredentialChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      hdmCredentials: { ...prev.hdmCredentials, [name]: value },
    }));
    setErrors((prev) => ({ ...prev, [name]: !value.trim() }));
  };

  const handleOperationToggle = (operation: TenantOperation) => {
    setFormData((prev) => {
      const isSelected = prev.operations.includes(operation);
      return {
        ...prev,
        operations: isSelected
          ? prev.operations.filter((op) => op !== operation)
          : [...prev.operations, operation],
      };
    });
  };

  const validateForm = (): boolean => {
    const newErrors = {
      name:
        formData.name.trim().length < 3 || formData.name.trim().length > 100,
      environmentId:
        formData.environmentId.trim().length < 1 ||
        formData.environmentId.trim().length > 20,
      archiveId: formData.archiveId <= 0,
      hdmApiEndpoint: !isValidAbsoluteUrl(formData.hdmApiEndpoint.trim()),
      username: !formData.hdmCredentials.username.trim(),
      password: !formData.hdmCredentials.password.trim(),
    };
    setErrors(newErrors);

    return Object.values(newErrors).every((error) => !error);
  };

  useEffect(() => {
    if (open) {
      setFormData(defaultCreateTenantRequest);
      setErrors({
        name: false,
        environmentId: false,
        archiveId: false,
        hdmApiEndpoint: false,
        username: false,
        password: false,
      });
    }
  }, [defaultCreateTenantRequest, open]);

  const handleSave = () => {
    if (validateForm()) {
      onSave(formData);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Create Tenant</DialogTitle>
      <DialogContent>
        <TextField
          required
          label="Name"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          fullWidth
          margin="normal"
          error={errors.name}
          helperText={
            errors.name ? "Name must be between 3 and 100 characters long." : ""
          }
        />
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            required
            label="Environment ID"
            name="environmentId"
            value={formData.environmentId}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
            error={errors.environmentId}
            helperText={
              errors.environmentId
                ? "Environment ID must be between 1 and 20 characters long."
                : ""
            }
          />
          <TextField
            required
            label="Archive ID"
            name="archiveId"
            type="number"
            value={formData.archiveId}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
            error={errors.archiveId}
            helperText={
              errors.archiveId
                ? "Archive ID must be a number greater than 0."
                : ""
            }
          />
        </Box>
        <TextField
          required
          label="HDM API Endpoint"
          name="hdmApiEndpoint"
          value={formData.hdmApiEndpoint}
          onChange={handleInputChange}
          fullWidth
          margin="normal"
          error={errors.hdmApiEndpoint}
          helperText={
            errors.hdmApiEndpoint ? "HDM API Endpoint is required" : ""
          }
        />
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            required
            label="HDM Credential Username"
            name="username"
            value={formData.hdmCredentials.username}
            onChange={handleHdmCredentialChange}
            fullWidth
            margin="normal"
            error={errors.username}
            helperText={errors.username ? "Username is required" : ""}
          />
          <TextField
            required
            label="HDM Credential Password"
            name="password"
            type="password"
            value={formData.hdmCredentials.password}
            onChange={handleHdmCredentialChange}
            fullWidth
            margin="normal"
            error={errors.password}
            helperText={errors.password ? "Password is required" : ""}
          />
        </Box>
        <div style={{ marginTop: "1rem" }}>
          <h4>Tenant Operations</h4>
          <FormGroup>
            {Object.values(TenantOperation).map((operation) => (
              <FormControlLabel
                key={operation}
                control={
                  <Checkbox
                    checked={formData.operations.includes(operation)}
                    onChange={() => handleOperationToggle(operation)}
                  />
                }
                label={operation}
              />
            ))}
          </FormGroup>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTenantDialog;
